import React, { useState, useRef, useEffect } from 'react';

const PopupMenu = ({ trigger, items, position = 'bottom-right' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleKeyDown = (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => (prev + 1) % items.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev - 1 + items.length) % items.length);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        items[focusedIndex].onClick();
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleItemClick = (item) => {
    item.onClick();
    setIsOpen(false);
  };

  const positionClasses = {
    'bottom-right': 'top-full right-0 mt-2',
    'bottom-left': 'top-full left-0 mt-2',
    'bottom-center': 'top-full left-1/2 -translate-x-1/2 mt-2',
    'top-right': 'bottom-full right-0 mb-2',
    'top-left': 'bottom-full left-0 mb-2',
    'top-center': 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    'left-center': 'right-full top-1/2 -translate-y-1/2 mr-2',
    'right-center': 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  const handleTriggerClick = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative inline-block" onKeyDown={handleKeyDown}>
      <div
        ref={triggerRef}
        onClick={handleTriggerClick}
        role="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {trigger}
      </div>

      {isOpen && (
        <div
          ref={menuRef}
          className={`
            absolute ${positionClasses[position]}
            bg-secondary border border-neutral-800 rounded-md shadow-2xl py-1 min-w-[160px] z-50 animate-scale-in
          `}
          role="menu"
        >
          {items.map((item, index) => {
            const variantClasses = {
              danger: 'text-red-400 hover:bg-red-500/10',
              default: 'text-text-muted hover:bg-neutral-800 hover:text-text',
            };

            return (
              <button
                key={index}
                onClick={() => handleItemClick(item)}
                className={`
                  w-full px-3 py-1.5 text-left text-xs flex items-center gap-3
                  ${variantClasses[item.variant || 'default']}
                  ${focusedIndex === index ? 'bg-neutral-800' : ''}
                  transition-colors
                `}
                role="menuitem"
                tabIndex={focusedIndex === index ? 0 : -1}
              >
                {item.icon && <span className="flex items-center justify-center">{item.icon}</span>}
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PopupMenu;
